import { useEffect, useMemo, useState } from "react";
import { X, CaretLeft, CaretRight, ClockCounterClockwise, Link as LinkIcon, CheckCircle, Info } from "@phosphor-icons/react";
import { ExerciseLogForm } from "./ExerciseLogForm";
import { buildSupersetSegments } from "./superset";
import { RestTimer } from "./RestTimer";
import WorkoutSummaryScreen from "./WorkoutSummaryScreen";
import SharedExercisePreview from "./SharedExercisePreview";
import { buildSummary, readWorkoutStart, clearWorkoutStart } from "@/lib/workoutSummary";

/**
 * ActiveWorkoutMode — full-screen "training" experience for the client.
 *
 * Opens when the user taps "Inizia" on a workout row. Shows ONE block at a
 * time (either a single exercise or an entire superset). Reuses the existing
 * ExerciseLogForm so the input UX (custom wheel picker, autosave, last log
 * ghost data) is identical to the inline expansion in the workout list.
 *
 * The list view is untouched — this is a sibling screen the parent toggles.
 */
export default function ActiveWorkoutMode({ workout, token, logs, exerciseLibrary, onLogSaved, onClose, clientName }) {
  const blocks = useMemo(
    () => buildSupersetSegments(workout?.exercises || []),
    [workout]
  );
  const [blockIdx, setBlockIdx] = useState(0);
  const [summary, setSummary] = useState(null);
  const [preview, setPreview] = useState(null); // { ex, libraryEntry } — exercise details lightbox
  const total = blocks.length;
  const current = blocks[blockIdx];
  const isLastBlock = blockIdx >= total - 1;

  // Lock body scroll + ESC = ask to exit (confirm dialog).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Library lookup for "Ultima sessione" enrichment (kept consistent with
  // the inline list view).
  const libraryByName = useMemo(() => {
    const map = new Map();
    (exerciseLibrary || []).forEach((e) => {
      if (e?.name) map.set(e.name.trim().toLowerCase(), e);
    });
    return map;
  }, [exerciseLibrary]);

  const today = new Date().toISOString().split("T")[0];

  const findLibrary = (ex) =>
    libraryByName.get((ex?.name || "").trim().toLowerCase()) || null;

  const openPreview = (ex) => {
    setPreview({ ex, libraryEntry: findLibrary(ex) });
  };

  const getExerciseLog = (exName) =>
    logs.find(
      (l) => l.workout_id === workout.id && l.exercise_name === exName && l.date === today
    );

  const getPreviousLog = (exName) =>
    logs
      .filter((l) =>
        l.exercise_name === exName &&
        !(l.workout_id === workout.id && l.date === today)
      )
      .sort((a, b) => b.date.localeCompare(a.date))[0] || null;

  // Format the "last session" summary line. We pick the heaviest set of the
  // most recent log (max weight, max reps as tie-breaker).
  const lastSessionSummary = (exName) => {
    const prev = getPreviousLog(exName);
    if (!prev?.sets?.length) return null;
    let best = { weight_kg: 0, reps: 0 };
    prev.sets.forEach((s) => {
      const w = Number(s.weight_kg) || 0;
      const r = Number(s.reps) || 0;
      if (w > best.weight_kg || (w === best.weight_kg && r > best.reps)) {
        best = { weight_kg: w, reps: r };
      }
    });
    if (best.reps === 0 && best.weight_kg === 0) return null;
    return `${best.reps} × ${best.weight_kg} kg`;
  };

  const handleExit = () => {
    // Only ask confirmation when at least one set was filled in this session.
    const anyLogged = (workout.exercises || []).some((ex) => {
      const l = getExerciseLog(ex.name);
      return l?.sets?.some((s) => s.weight_kg || s.reps);
    });
    if (anyLogged && !window.confirm("Vuoi davvero uscire dall'allenamento? I dati già salvati non andranno persi.")) {
      return;
    }
    if (!anyLogged && !window.confirm("Uscire dall'allenamento?")) return;
    onClose?.();
  };

  const handleFinish = () => {
    if (!window.confirm("Termina questo allenamento?")) return;
    const startTs = readWorkoutStart(workout.id);
    const endTs = new Date().toISOString();
    const built = buildSummary({
      workout,
      logs,
      todayIso: today,
      startTs,
      endTs,
    });
    setSummary(built);
    clearWorkoutStart(workout.id);
  };

  // If a summary has been computed, show the summary screen instead of the
  // active workout view. The active workout markup below stays untouched.
  if (summary) {
    return (
      <WorkoutSummaryScreen
        workout={workout}
        clientName={clientName}
        summary={summary}
        onHome={onClose}
      />
    );
  }

  const progressPct = total > 0 ? ((blockIdx + 1) / total) * 100 : 0;

  return (
    <div
      className="fixed inset-0 z-[80] bg-[#0A0A0A] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-labelledby="active-workout-title"
      data-testid="active-workout-mode"
    >
      {/* Header */}
      <header className="border-b border-[#1A1A1A] bg-[#0F0F0F] px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={handleExit}
          className="w-9 h-9 rounded-full bg-[#1A1A1A] hover:bg-[#262626] text-zinc-400 hover:text-white flex items-center justify-center flex-shrink-0"
          aria-label="Esci dall'allenamento"
          data-testid="active-workout-exit"
        >
          <X size={14} weight="bold" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#007AFF]">In corso</p>
          <h1
            id="active-workout-title"
            className="text-base font-bold tracking-tight text-white uppercase truncate"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            {workout?.title || "Allenamento"}
          </h1>
        </div>
        <div className="flex-shrink-0">
          <RestTimer shareToken={token} />
        </div>
      </header>

      {/* Progress bar */}
      <div className="bg-[#0F0F0F] border-b border-[#1A1A1A] px-4 pt-2 pb-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Blocco</span>
          <span className="text-[11px] font-bold text-white tabular-nums">{blockIdx + 1}/{total}</span>
        </div>
        <div className="relative h-1 rounded-full bg-[#1A1A1A] overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#007AFF] to-[#D4FF00] rounded-full transition-all"
            style={{ width: `${progressPct}%` }}
            data-testid="active-workout-progress"
          />
        </div>
      </div>

      {/* Block card */}
      <main className="flex-1 overflow-y-auto px-4 py-5">
        <div className="max-w-md mx-auto">
          {current && (
            current.isSuperset
              ? <SupersetBlock
                  key={`ss-${current.supersetId}-${current.items[0].idx}`}
                  segment={current}
                  workoutId={workout.id}
                  token={token}
                  getExerciseLog={getExerciseLog}
                  getPreviousLog={getPreviousLog}
                  lastSessionSummary={lastSessionSummary}
                  onLogSaved={onLogSaved}
                  onPreview={openPreview}
                />
              : <SingleExerciseBlock
                  key={`ex-${current.items[0].idx}-${current.items[0].ex.name}`}
                  ex={current.items[0].ex}
                  exerciseIndex={current.items[0].idx}
                  workoutId={workout.id}
                  token={token}
                  existingLog={getExerciseLog(current.items[0].ex.name)}
                  previousLog={getPreviousLog(current.items[0].ex.name)}
                  lastSessionSummary={lastSessionSummary(current.items[0].ex.name)}
                  onLogSaved={onLogSaved}
                  onPreview={openPreview}
                />
          )}
        </div>
      </main>

      {/* Navigation row */}
      <nav className="border-t border-[#1A1A1A] bg-[#0F0F0F] px-4 py-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setBlockIdx((i) => Math.max(0, i - 1))}
          disabled={blockIdx === 0}
          className="flex-1 h-12 rounded-xl bg-[#1A1A1A] border border-[#262626] text-white font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
          data-testid="active-workout-prev"
        >
          <CaretLeft size={16} weight="bold" /> Indietro
        </button>
        {isLastBlock ? (
          <button
            type="button"
            onClick={handleFinish}
            className="flex-1 h-12 rounded-xl bg-[#34C759] hover:bg-[#43D971] text-white font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"
            data-testid="active-workout-finish"
          >
            <CheckCircle size={16} weight="fill" /> Termina workout
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setBlockIdx((i) => Math.min(total - 1, i + 1))}
            className="flex-1 h-12 rounded-xl bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"
            data-testid="active-workout-next"
          >
            Prossimo <CaretRight size={16} weight="bold" />
          </button>
        )}
      </nav>

      {preview && (
        <SharedExercisePreview
          exercise={preview.ex}
          libraryEntry={preview.libraryEntry}
          token={token}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

/* ----------------------------- Sub-cards ----------------------------- */

function ExerciseHead({ label, name, ex, lastSummary, onPreview }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-start justify-between gap-3">
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#007AFF]/15 text-[#007AFF] font-bold uppercase tracking-wider">
          {label}
        </span>
        {onPreview && (
          <button
            type="button"
            onClick={onPreview}
            aria-label="Mostra dettagli esercizio"
            className="text-zinc-500 hover:text-[#007AFF] active:scale-95 transition-all cursor-pointer p-1 -m-1 rounded flex-shrink-0"
            data-testid={`active-exercise-info-${name}`}
          >
            <Info size={18} />
          </button>
        )}
      </div>
      <h2
        className="text-xl font-black tracking-tight text-white uppercase"
        style={{ fontFamily: "Barlow Condensed, sans-serif" }}
      >
        {name}
      </h2>
      <div className="flex items-center gap-3 text-[11px] flex-wrap">
        <span className="text-[#007AFF] font-bold">{ex.sets} serie</span>
        <span className="text-[#D4FF00] font-bold">{ex.reps} reps</span>
        {ex.rest_seconds > 0 && (
          <span className="text-zinc-500">{ex.rest_seconds}s rec.</span>
        )}
      </div>
      {lastSummary && (
        <p className="text-[11px] text-zinc-400 flex items-center gap-1.5 pt-1">
          <ClockCounterClockwise size={12} className="text-zinc-500" />
          Ultima sessione: <span className="text-white font-semibold">{lastSummary}</span>
        </p>
      )}
    </div>
  );
}

function SingleExerciseBlock({ ex, exerciseIndex, workoutId, token, existingLog, previousLog, lastSessionSummary, onLogSaved, onPreview }) {
  return (
    <div
      className="bg-[#171717] border border-[#262626] rounded-2xl p-5 space-y-4"
      data-testid={`active-block-exercise-${exerciseIndex}`}
    >
      <ExerciseHead
        label={`Esercizio ${exerciseIndex + 1}`}
        name={ex.name}
        ex={ex}
        lastSummary={lastSessionSummary}
        onPreview={onPreview ? () => onPreview(ex) : undefined}
      />
      {ex.notes && (
        <div className="p-2.5 bg-[#0A0A0A] rounded-lg border border-[#007AFF]/20">
          <p className="text-[10px] text-[#007AFF] uppercase tracking-wider font-bold mb-1">Nota del coach</p>
          <p className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed">{ex.notes}</p>
        </div>
      )}
      <div className="border-t border-[#1A1A1A] -mx-5" />
      <ExerciseLogForm
        exercise={ex}
        workoutId={workoutId}
        token={token}
        existingLog={existingLog}
        previousLog={previousLog}
        onSaved={onLogSaved}
      />
    </div>
  );
}

function SupersetBlock({ segment, workoutId, token, getExerciseLog, getPreviousLog, lastSessionSummary, onLogSaved, onPreview }) {
  return (
    <div
      className="bg-[#171717] border border-[#AF52DE]/30 rounded-2xl p-5 space-y-4"
      data-testid={`active-block-superset-${segment.supersetId}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#AF52DE]/15 text-[#AF52DE] font-bold uppercase tracking-wider flex items-center gap-1">
          <LinkIcon size={9} weight="bold" /> Superset {segment.supersetId}
        </span>
      </div>
      <p className="text-[11px] text-zinc-500 italic -mt-2">
        Eseguiti di fila senza recupero
      </p>
      <div className="space-y-5">
        {segment.items.map(({ ex, idx: j }, subIdx) => (
          <div key={`ssitem-${j}`} data-testid={`active-superset-item-${j}`}>
            {subIdx > 0 && <div className="border-t border-[#1A1A1A] -mx-5 my-4" />}
            <ExerciseHead
              label={`${segment.supersetId}${subIdx + 1} · ${ex.name ? "" : ""}`.trim() || `${segment.supersetId}${subIdx + 1}`}
              name={ex.name}
              ex={ex}
              lastSummary={lastSessionSummary(ex.name)}
              onPreview={onPreview ? () => onPreview(ex) : undefined}
            />
            {ex.notes && (
              <div className="mt-2 p-2.5 bg-[#0A0A0A] rounded-lg border border-[#007AFF]/20">
                <p className="text-[10px] text-[#007AFF] uppercase tracking-wider font-bold mb-1">Nota del coach</p>
                <p className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed">{ex.notes}</p>
              </div>
            )}
            <ExerciseLogForm
              exercise={ex}
              workoutId={workoutId}
              token={token}
              existingLog={getExerciseLog(ex.name)}
              previousLog={getPreviousLog(ex.name)}
              onSaved={onLogSaved}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
